
import { defineComponent, ref, watchEffect, computed } from 'vue'
import Spinner from '@/components/Spinner.vue'
import GenericError from '@/components/GenericError.vue'
import PageHeader from '@/components/PageHeader.vue'
import useTitle from '@/composables/useTitle'
import { markContentPending, markContentReady } from '@/composables/useRobots'
import router from '@/router'
import CkeditorModule from '@/components/CkeditorModule.vue'
import { getPage, Page } from '@/kentico-api/submenus.api'
import { AxiosResponse } from 'axios'
import { onBeforeRouteUpdate } from 'vue-router'
import { useHead } from '@vueuse/head'
import RichTextRender from '@/components/kontent/RichTextRender.vue'
import imageUrlBuilder from '@/kentico-api/imageUrlBuilder'

export default defineComponent({
  components: { Spinner, GenericError, PageHeader, CkeditorModule, RichTextRender },
  props: {
    slug: {
      type: String,
      required: true
    },
    redirectOnError: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  // eslint-disable-next-line max-lines-per-function
  setup (props) {
    try {
      const { setTitle } = useTitle()
      const page = ref()
      const error = ref()

      const loadPageContent = (slug: string) => {
        // Hold `noindex` until the CMS content is in: a spinner or an error
        // page must never be indexed as if it were the real page.
        markContentPending(`/${slug}`)
        getPage(slug)
          .then(({ data }: AxiosResponse<Page>) => {
            // The CMS response always carries a `modularContent` key, so an
            // empty result is only detectable by the absence of the page.
            if (!data.path) {
              if (props.redirectOnError) {
                router.replace({ name: 'Not Found' })
                return
              }
              error.value = true
              return
            }

            page.value = data
            markContentReady()
          })
          .catch(e => {
            error.value = e
          })
      }

      loadPageContent(props.slug)

      onBeforeRouteUpdate(to => { loadPageContent(to.params.slug as string) })

      watchEffect(() => setTitle(page.value?.title))

      useHead({
        meta: [
          {
            name: 'og:description',
            content: computed(() => page.value?.openGraphOgDescription || ''),
            key: 'ogDescription'
          },
          {
            name: 'og:title',
            content: computed(() => page.value?.openGraphOgTitle || ''),
            key: 'ogTitle'
          },
          {
            name: 'og:url',
            content: `${process.env.VUE_APP_SITE_URL}${props.slug}`,
            key: 'ogUrl'
          },
          {
            name: 'og:image',
            itemprop: 'image',
            content: computed(() => page.value && page.value.openGraphOgImage?.length > 0
              ? imageUrlBuilder(page.value.openGraphOgImage[0].url, { w: 1200, auto: 'format' })
              : `${process.env.VUE_APP_SITE_URL}images/expressway-image.jpeg`),
            key: 'ogImage'
          },
          {
            name: 'og:locale',
            content: computed(() => page.value?.openGraphOgLocale || 'en_IE'),
            key: 'ogLocale'
          }
        ]
      })
      return { page, error }
    } catch (error) {
      return { error: true }
    }
  }
})
